/**
 * The router's fallback for anything thrown inside a route.
 *
 * ## Why this exists as well as `ErrorBoundary`
 *
 * TanStack Router wraps every route in its own catch boundary, so a render
 * throw inside a route component is caught *there* — before it can reach the
 * `ErrorBoundary` mounted outside the providers in `main.tsx`. Without a
 * `defaultErrorComponent` the router falls back to its own built-in, which is
 * unstyled, off-brand, and reports nothing.
 *
 * That last part is the reason this is worth a file. `ErrorBoundary`'s whole
 * argument is that React swallows render errors before `window.onerror` sees
 * them, so a boundary that does not report leaves the errors that actually
 * break the UI invisible in the dashboard. A router boundary that does not
 * report has exactly the same hole, one layer down — and it covers the routes,
 * which is where the recipe, batch and brew-day screens live.
 *
 * ## Two ways out, and why both
 *
 * A whole-app crash gets one button, because only a reload can help. A *route*
 * error is different: the app around it is still alive, its providers are up
 * and the workspace is loaded, so navigating away genuinely escapes a broken
 * screen without paying for a reload.
 *
 * Both are offered because neither alone is honest. "Back to recipes" is the
 * cheap escape but loops if the recipes route is the one that threw; reload
 * always works but throws away a warm session to do it.
 *
 * Deliberately *not* offered: the router's `reset`. It re-renders the same
 * route from the same state, which re-throws immediately — the button that
 * does nothing, which `ErrorBoundary` already refuses for the same reason.
 */

import type { ErrorComponentProps } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { CrashPage } from '@/components/error-boundary';
import { Button } from '@/components/ui/button';
import { reportError } from '@/lib/posthog';

export interface RouteErrorProps extends Pick<ErrorComponentProps, 'error'> {
  /** Where a caught error goes. Injectable for the same reason it is on `ErrorBoundary`. */
  readonly onError?: (error: unknown) => void;
}

export function RouteError({ error, onError }: RouteErrorProps) {
  // Keyed on the error so a re-render — a theme change, a resize — does not
  // report the same crash again. The router keeps this mounted until the user
  // navigates, so without the key one crash could become dozens of events.
  useEffect(() => {
    // Not redundant with the report: a development build has no PostHog key,
    // and would otherwise show a fallback page with the stack nowhere.
    console.error('[beerwright] unhandled route error', error);
    (onError ?? reportError)(error);
  }, [error, onError]);

  return (
    <CrashPage
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button asChild>
            <Link to="/recipes">Back to recipes</Link>
          </Button>
          <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
            Reload Beerwright
          </Button>
        </div>
      }
    />
  );
}
